// src/authpages/group/groupUtils.js
import { 
  auth,
  db, 
  doc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  collection,
  addDoc,
  increment,
  serverTimestamp 
} from '../../firebase';

// Study methods data
export const studyMethods = [
  {
    name: "Peer Teaching",
    description: "Group members take turns explaining concepts to each other.",
    recommendedPeople: "3-6 people",
    recommendedTime: "1-2 hours per session",
    benefits: [
      "Deepens understanding through explanation",
      "Identifies and fills knowledge gaps",
      "Improves communication skills",
      "Helps learn from different perspectives"
    ],
    howToDo: [
      "Assign each group member a specific topic or concept to prepare",
      "Give 10-15 minutes for individual preparation",
      "Take turns presenting the topic to the group (5-10 minutes per person)",
      "Encourage questions and discussions after each presentation",
      "Provide constructive feedback and clarify misunderstandings",
      "End with a summary connecting all concepts presented"
    ],
    example: {
      context: "Computer Science Study Group",
      scenario: "A group of 4 CS students studying data structures focuses on linked lists. They allocate 90 minutes for their session:\n\n• Preparation (15 min): Each member researches their assigned aspect\n• Member A (10 min): Explains the basic concept and structure of linked lists\n• Discussion (5 min): Group asks clarifying questions\n• Member B (10 min): Demonstrates code implementation with examples\n• Discussion (5 min): Group discusses implementation challenges\n• Member C (10 min): Presents time and space complexity analysis\n• Discussion (5 min): Group explores optimization possibilities\n• Member D (10 min): Shares real-world applications and use cases\n• Final discussion (20 min): Group integrates all concepts and solves a practice problem together"
    }
  },
  {
    name: "Round Robin Discussion",
    description: "Structured group discussion where each member contributes insights.",
    recommendedPeople: "4-8 people",
    recommendedTime: "30-60 minutes per session",
    benefits: [
      "Ensures equal participation",
      "Exposes different viewpoints",
      "Develops critical thinking skills",
      "Builds collaborative learning environment"
    ],
    howToDo: [
      "Choose a central topic or problem to discuss",
      "Set a time limit for the entire discussion (30-60 minutes)",
      "Establish a clockwise or counterclockwise speaking order",
      "Each member gets 2-3 minutes to share thoughts without interruption",
      "Allow brief clarifying questions after each person speaks",
      "After the first round, conduct a second round for responses to others' points",
      "Conclude with a group summary of key insights"
    ],
    example: {
      context: "History Study Group",
      scenario: "A group of 5 history students discussing the causes of World War II plans a 45-minute session:\n\n• Introduction (5 min): Group leader outlines the discussion topic and sets ground rules\n• First round (15 min):\n  - Member 1 (3 min): Presents economic factors in Germany\n  - Member 2 (3 min): Discusses political climate in Europe\n  - Member 3 (3 min): Analyzes rise of fascism\n  - Member 4 (3 min): Explains global diplomatic tensions\n  - Member 5 (3 min): Covers the role of previous conflicts\n• Second round (15 min): Each member responds to points raised by others\n• Synthesis (10 min): Group collectively identifies the most significant causes and their interconnections"
    }
  },
  {
    name: "Collaborative Problem Solving",
    description: "Tackle complex problems together through collective brainstorming.",
    recommendedPeople: "3-5 people",
    recommendedTime: "1-3 hours depending on problem complexity",
    benefits: [
      "Combines diverse problem-solving approaches",
      "Breaks down complex challenges",
      "Enhances teamwork and communication",
      "Provides mutual support and motivation"
    ],
    howToDo: [
      "Select a complex problem or challenging assignment",
      "Break the problem into smaller, manageable components",
      "Assign different components to group members or work on them together",
      "Use a shared document or whiteboard for collaboration",
      "Regular check-ins to discuss progress and challenges",
      "Collaborative solution development and review",
      "Conduct a final group review and refinement"
    ],
    example: {
      context: "Mathematics Study Group",
      scenario: "A group of 4 calculus students tackles an optimization problem over a 2-hour session:\n\n• Problem analysis (20 min): The group reads through the problem together and identifies key components\n• Strategy development (15 min): Brainstorming possible approaches\n• Division of labor (5 min):\n  - Member 1: Sets up the initial problem framework and constraints\n  - Member 2: Develops potential solution strategies\n  - Member 3: Performs mathematical calculations\n  - Member 4: Verifies the mathematical logic\n• Individual/pair work (40 min): Members work on their assigned components\n• Progress check-in (10 min): Group shares progress and challenges\n• Collaborative refinement (20 min): Group works together to integrate components\n• Solution review (10 min): Final verification and error checking"
    }
  }
];

// Timer methods from StudyTimers.js
export const timerMethods = [
  {
    name: "Pomodoro",
    description: "25 minutes of focused work, followed by 5-minute breaks. After 4 sessions, take a longer 15-minute break.",
    workTime: 25,
    breakTime: 5,
    longBreakTime: 15,
    longBreakInterval: 4
  },
  {
    name: "Flow Master",
    description: "52 minutes of deep work followed by 17 minutes of rest. Optimized for achieving flow state.",
    workTime: 52,
    breakTime: 17
  },
  {
    name: "Energy Cycle",
    description: "90 minutes of focused work aligned with your natural energy cycles, followed by 20 minutes of recovery.",
    workTime: 90,
    breakTime: 20
  },
  {
    name: "Peak Focus",
    description: "58 minutes of intensive focus followed by 17 minutes of rest. Based on productivity research.",
    workTime: 58,
    breakTime: 17
  }
];

// Improved createStudyGroup function with better error handling
export const createStudyGroup = async (groupData, db) => {
  try {
    // Step 1: Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('You must be logged in to create a group');
    }

    // Step 2: Remove any pre-set ID (let Firestore generate it)
    const { id, ...groupDataWithoutId } = groupData;
    
    // Step 3: Create the study group document with explicit creator field
    const groupsCollection = collection(db, 'studyGroups');
    const newGroupData = {
      ...groupDataWithoutId,
      creator: currentUser.uid,
      createdAt: serverTimestamp(),
      lastActive: serverTimestamp(),
      // Make sure members and other required fields are properly set
      members: Array.isArray(groupDataWithoutId.members) 
        ? groupDataWithoutId.members 
        : [currentUser.uid],
      memberCount: groupDataWithoutId.memberCount || groupDataWithoutId.members.length
    };
    
    console.log('Creating study group with data:', newGroupData);
    
    // Step 4: Add the document to Firestore and get the generated ID
    const docRef = await addDoc(groupsCollection, newGroupData);
    const generatedId = docRef.id;
    console.log('Study group created with Firestore ID:', generatedId);
    
    // Step 5: Update the document with its own ID for reference
    await updateDoc(docRef, { id: generatedId });
    
    // Step 6: ONLY update the current user's document
    try {
      // First update the creator's document
      const currentUserRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(currentUserRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const studyGroups = userData.studyGroups || [];
        
        if (!studyGroups.includes(generatedId)) {
          await updateDoc(currentUserRef, {
            studyGroups: arrayUnion(generatedId)
          });
        }
      }
      
      // Then update other members' documents
      const otherMembers = newGroupData.members.filter(
        memberId => memberId !== currentUser.uid
      );
      
      for (const memberId of otherMembers) {
        try {
          const memberRef = doc(db, 'users', memberId);
          const memberDoc = await getDoc(memberRef);
          
          if (memberDoc.exists()) {
            const memberData = memberDoc.data();
            const memberGroups = memberData.studyGroups || [];
            
            if (!memberGroups.includes(generatedId)) {
              await updateDoc(memberRef, {
                studyGroups: arrayUnion(generatedId)
              });
            }
          }
        } catch (memberError) {
          console.error(`Error updating member ${memberId} document:`, memberError);
          // Continue with other members
        }
      }
      
      console.log('Updated all user documents with group ID');
    } catch (userUpdateError) {
      console.error('Error updating user documents:', userUpdateError);
    }
    
    // Return the created group data with the Firestore-generated ID
    return {
      ...newGroupData,
      id: generatedId
    };
  } catch (error) {
    console.error('Error creating study group:', error);
    console.log('Error code:', error.code);
    console.log('Error message:', error.message);
    throw error;
  }
};

// Improved joinStudyGroup function with better error handling
export const joinStudyGroup = async (userId, groupId, db) => {
  try {
    // Check if group exists first
    const groupRef = doc(db, 'studyGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      console.error(`Group with ID ${groupId} not found`);
      throw new Error('Group not found');
    }
    
    const groupData = groupDoc.data();
    
    // Check if group is full
    if (groupData.memberCount >= groupData.maxMembers) {
      throw new Error('Group is full');
    }
    
    // Check if user is already a member
    if (groupData.members.includes(userId)) {
      console.log(`User ${userId} is already a member of group ${groupId}`);
      return true; // Already a member, consider this a success
    }
    
    // Add user to group
    await updateDoc(groupRef, {
      members: arrayUnion(userId),
      memberCount: increment(1),
      lastActive: serverTimestamp()
    });
    
    // Add group to user's groups
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      await updateDoc(userRef, {
        studyGroups: arrayUnion(groupId)
      });
    } else {
      console.error(`User document with ID ${userId} not found`);
    }
    
    return true;
  } catch (error) {
    console.error('Error joining study group:', error);
    throw error;
  }
};

// Improved leaveStudyGroup function with better error handling
export const leaveStudyGroup = async (userId, groupId, db) => {
  try {
    // First check if the group exists
    const groupRef = doc(db, 'studyGroups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      console.log(`Group with ID ${groupId} not found - might have been already deleted`);
      
      // Even if group doesn't exist, we should still try to clean up the user's document
      try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Check if user has this group in their array
          if (userData.studyGroups && userData.studyGroups.includes(groupId)) {
            await updateDoc(userRef, {
              studyGroups: userData.studyGroups.filter(g => g !== groupId)
            });
            console.log(`Cleaned up non-existent group ${groupId} from user ${userId}`);
          }
        }
      } catch (userError) {
        console.error(`Error cleaning up user document for ${userId}:`, userError);
      }
      
      // Return without throwing an error - consider this a success since the group is gone
      return true;
    }
    
    // Group exists, proceed with normal removal
    // Remove user from group
    await updateDoc(groupRef, {
      members: arrayRemove(userId),
      memberCount: increment(-1),
      lastActive: serverTimestamp()
    });
    
    // Remove group from user's groups
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      await updateDoc(userRef, {
        studyGroups: arrayRemove(groupId)
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error leaving study group:', error);
    throw error;
  }
};

// Improved fetchUserGroups function to prevent duplicates
export const fetchUserGroups = async (userId, db) => {
  try {
    // Get user's groups IDs
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log(`User document for ${userId} not found`);
      return [];
    }
    
    const userData = userDoc.data();
    
    // Remove any duplicate group IDs that might exist in the user's document
    const uniqueGroupIds = [...new Set(userData.studyGroups || [])];
    
    if (uniqueGroupIds.length === 0) {
      return [];
    }
    
    console.log(`Fetching ${uniqueGroupIds.length} unique group IDs for user ${userId}`);
    
    // Fetch each group's data
    const groupsData = await Promise.all(
      uniqueGroupIds.map(async (groupId) => {
        try {
          const groupDoc = await getDoc(doc(db, 'studyGroups', groupId));
          if (groupDoc.exists()) {
            return {
              id: groupId,
              ...groupDoc.data()
            };
          }
          console.log(`Group ${groupId} referenced in user document but doesn't exist in Firestore`);
          return null;
        } catch (groupError) {
          console.error(`Error fetching group ${groupId}:`, groupError);
          return null;
        }
      })
    );
    
    // Filter out any null values (groups that couldn't be fetched)
    // and ensure there are no duplicates based on id
    const filteredGroups = groupsData.filter(Boolean);
    
    // Create a Map with group ID as key to eliminate potential duplicates
    const uniqueGroupsMap = new Map();
    filteredGroups.forEach(group => {
      if (!uniqueGroupsMap.has(group.id)) {
        uniqueGroupsMap.set(group.id, group);
      }
    });
    
    return Array.from(uniqueGroupsMap.values());
  } catch (error) {
    console.error('Error fetching user groups:', error);
    throw error;
  }
};